
import { Vue, Component } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatReviewsDetails from '@/components/qa/chat/ChatReviewsDetails.vue';
import ChatEscalationReview from '@/components/qa/escalation/ChatEscalationReview.vue';
import EscalationDetails from '@/components/qa/escalation/EscalationDetails.vue';
import EscalationsFilters from '@/components/qa/escalation/EscalationsFilters.vue';
import ChatMessages from '@/components/qa/chat/ChatMessages.vue';
import ChatPrivateNotes from '@/components/qa/chat/ChatPrivateNotes.vue';
import ChatDetails from '@/components/qa/chat/ChatDetails.vue';
import {
  ChatEscalationInterface,
  EscalationRepositoryInterface,
  EscalationStatus,
} from '@/services/QC-V2/escalation/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { ChatInterface, ChatReviewInterface, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import EscalationReason from '@/components/qa/escalation/EscalationReason.vue';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'userRepository',
    'escalationRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
  ],
  components: {
    EscalationReason,
    ChatReviewsDetails,
    ChatEscalationReview,
    EscalationDetails,
    EscalationsFilters,
    ChatMessages,
    ChatPrivateNotes,
    ChatDetails,
  },
})
export default class ChatEscalation extends Vue {
  private escalationRepositoryV2!: EscalationRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private escalation: ChatEscalationInterface = {} as ChatEscalationInterface;

  private chat: ChatInterface = {} as ChatInterface;

  private profiles: ProfilesSelectInterface[] = [];
  private profilesMap: ProfileMapInterface = {};

  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[];
  private tags: TagInterface[] = [];

  private loading: boolean = true;

  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private async created(): Promise<void> {
    try {
      if (!this.$route.params.id) {
        this.$errorDialog.show('Escalation id not provided!');
        return;
      }

      await this.fetchScores();
      await this.fetchTags();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(): Promise<void> {
    try {
      const escalation = await this.escalationRepositoryV2.findChatEscalationById(Number(this.$route.params.id));
      if (!escalation) {
        this.$errorDialog.show(`Escalation with id ${this.$route.params.id} not found!`);
        return;
      }

      await this.loadChat(escalation);
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();
  }

  private async loadChat(escalation: ChatEscalationInterface): Promise<void> {
    await this.fetchChatProfilesData(escalation);
    this.escalation = escalation;
    this.chat = escalation.chat;
    await this.changeEscalationStatus();
  }

  private fetchEscalationUsersData(escalation: ChatEscalationInterface): string[] {
    const ids: string[] = [];
    ids.push(escalation.escalatedFrom);

    if (escalation.departmentManager) {
      ids.push(escalation.departmentManager);
    }

    if (escalation.shiftManager) {
      ids.push(escalation.shiftManager);
    }

    return ids;
  }

  private async fetchChatProfilesData(escalation: ChatEscalationInterface): Promise<void> {
    this.profiles = [];

    const idsForReview = await this.extractUserIdsForReview(escalation.chat);
    const idsFromChatData = this.extractUseIdsFromChatData(escalation.chat);
    const existingReviewIds = this.extractExistingReviewUsersIds(escalation.chat);
    const idsFromEscalation = this.fetchEscalationUsersData(escalation);

    const profiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(
        [...idsFromChatData, ...idsForReview, ...existingReviewIds, ...idsFromEscalation]
          .filter((id, index, self) => self.indexOf(id) === index),
      );

    this.profiles.push({
      id: 'chat',
      name: 'Chat Overall',
    });

    const profilesForReview = profiles.filter((profile) => idsForReview.includes(profile.userId))
      .map((profile) => ({
        id: profile.userId,
        name: `${profile.firstName} ${profile.lastName}`,
      }));

    this.profiles.push(...profilesForReview);

    const [firstName, lastName] = this.$store.getters.name.split(' ');
    this.profilesMap[this.$store.getters.id] = {
      userId: this.$store.getters.id,
      profileId: '',
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
    };

    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private extractUseIdsFromChatData(chat: ChatInterface): string[] {
    const ids: string[] = [];
    chat.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    chat.notes.forEach((note) => {
      if (!ids.includes(note.author)) {
        ids.push(note.author);
      }
    });

    chat.messages.forEach((message) => {
      if (!message.comments.length) {
        return;
      }

      message.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    return ids;
  }

  private extractExistingReviewUsersIds(chat: ChatInterface): string[] {
    if (!chat.reviews.length) {
      return [];
    }

    const ids: string[] = [];
    chat.reviews.forEach((review) => {
      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewerId);
      }

      if (!review.comments.length) {
        return;
      }

      review.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    return ids;
  }

  private async extractUserIdsForReview(chat: ChatInterface): Promise<string[]> {
    const ids: string[] = [];
    const emails = chat.agents.map((agent) => agent.email);
    emails.push(
      ...chat.supervisors.map((supervisor) => supervisor.email),
    );

    const users = await this.userRepository.getUsersByEmails(emails);

    users.forEach((user) => {
      if (!ids.includes(user.id)) {
        ids.push(user.id);
      }
    });

    return ids;
  }

  private async changeEscalationStatus(): Promise<void> {
    const viewer = this.$store.getters.id;
    const isEscalatedToCurrentUser = viewer === this.escalation.departmentManager
      || viewer === this.escalation.shiftManager;

    if (isEscalatedToCurrentUser && this.escalation.status === EscalationStatus.OPEN) {
      try {
        await this.escalationRepositoryV2.changeEscalationStatus(this.escalation.id, EscalationStatus.IN_PROGRESS);
        this.escalation.status = EscalationStatus.IN_PROGRESS;
      } catch (e) {
        this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
      }
    }
  }

  private async reviewEscalation(): Promise<void> {
    if (this.escalation.status === EscalationStatus.REVIEWED) {
      return;
    }
    this.escalation.status = EscalationStatus.REVIEWED;
  }

  private handleFeedbackCreate(reviews: ChatReviewInterface[]): void {
    this.escalation.chat.reviews = reviews;
    this.chat.reviews = reviews;
  }
}
